// KidCardDeck — the kid's pet-feeding deck under the pet on the home
// tab. Four card stacks (Super / Build / Boost / Spark) each showing
// the kid's current balance. Tap a stack with count > 0 to spend a
// card — calls spend_kid_card RPC, which decrements the count AND
// flips the matching has_X boolean (driving the 16-state alien art).
//
// Optimistic local count update for responsiveness; the realtime
// subscription on kid_pet_state (in useKidPetState) will reconcile
// the truth back. Disabled-with-dim state when count=0 (no scary
// "ASK A PARENT" copy — cards arrive when they arrive).
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**)
// is the explicit opt-in to emojis per the plan.

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { COLOR, FONT } from "../../lib/tokens";
import { KID_CARD_TYPES } from "./cardTypes";
import { playKidSfx } from "../../lib/kidSfx";
import { playKidVoice } from "../../lib/kidVoice";
import { statLabelForCardType, STAT_DEF_BY_CARD_TYPE } from "./petStats";

const CARDS = KID_CARD_TYPES;

export default function KidCardDeck({ pet, userId }) {
  // Optimistic local counts seeded from `pet`. Realtime sub on
  // kid_pet_state re-fires useKidPetState.load, which refreshes
  // `pet` and re-syncs.
  const [counts, setCounts] = useState({
    boost: pet?.cards_boost ?? 0,
    spark: pet?.cards_spark ?? 0,
    build: pet?.cards_build ?? 0,
    super: pet?.cards_super ?? 0,
    water: pet?.cards_water ?? 0,
    dairy: pet?.cards_dairy ?? 0,
  });
  const [spending, setSpending] = useState(null);
  const [pulse, setPulse] = useState(null);
  // levelUp = { from, to } when the most recent spend rolled the
  // pet's level. Drives the LEVEL UP! flash that overlays the deck
  // for ~1.6s. Cleared by a setTimeout so the kid doesn't have to
  // dismiss it.
  const [levelUp, setLevelUp] = useState(null);
  // statUp = { label, tint, emoji } when a spend rolled a fighter
  // stat (every 5 cards of a type). Shorter, smaller flash than
  // levelUp so multiple stat-ups don't feel as climactic as a full
  // level — but the kid still notices the moment.
  const [statUp, setStatUp] = useState(null);

  useEffect(() => {
    setCounts({
      boost: pet?.cards_boost ?? 0,
      spark: pet?.cards_spark ?? 0,
      build: pet?.cards_build ?? 0,
      super: pet?.cards_super ?? 0,
      water: pet?.cards_water ?? 0,
      dairy: pet?.cards_dairy ?? 0,
    });
  }, [pet?.cards_boost, pet?.cards_spark, pet?.cards_build, pet?.cards_super, pet?.cards_water, pet?.cards_dairy]);

  const total = counts.boost + counts.spark + counts.build + counts.super + counts.water + counts.dairy;

  const spend = async (cardKey) => {
    if (!userId || spending) return;
    if ((counts[cardKey] ?? 0) <= 0) return;
    setSpending(cardKey);
    // Optimistic decrement so the tap feels instant.
    setCounts((c) => ({ ...c, [cardKey]: Math.max(0, (c[cardKey] || 0) - 1) }));
    const { data, error } = await supabase.rpc("spend_kid_card", {
      p_kid_user_id: userId,
      p_card_type:   cardKey,
    });
    setSpending(null);
    if (error) {
      // Roll back the optimistic update on error.
      setCounts((c) => ({ ...c, [cardKey]: (c[cardKey] || 0) + 1 }));
      console.warn("[kid-card-deck] spend failed:", error.message);
      return;
    }
    // Brief glow pulse to reward the spend; the actual pet art change
    // arrives through the realtime path.
    setPulse(cardKey);
    setTimeout(() => setPulse((cur) => (cur === cardKey ? null : cur)), 600);

    // Munch SFX on every spend — the pet just ate the card.
    playKidSfx("munch");
    // Voice announcement on every card spend so the kid hears
    // the card name. Water has no clip yet — playKidVoice no-ops.
    playKidVoice(cardKey);

    // Migration 0245 — spend_kid_card now returns level/xp_in_level
    // and a leveled_up flag. When the spend rolled the pet's level,
    // flash a LEVEL UP overlay over the deck for ~1.6s + play the
    // fanfare so the kid hears the celebration even if their eye
    // wasn't on the level bar. RPC returns an array (RETURNS TABLE);
    // take the first row.
    const row = Array.isArray(data) ? data[0] : data;
    if (row?.leveled_up && typeof row.new_level === "number") {
      setLevelUp({ to: row.new_level });
      // Fanfare comes ~120ms after the munch so the two beats don't
      // step on each other.
      setTimeout(() => playKidSfx("fanfare"), 120);
      setTimeout(() => setLevelUp((cur) => (cur?.to === row.new_level ? null : cur)), 1600);
    }

    // Migration 0246 — stat_up=true means this spend rolled a new
    // fighter stat point (every 5 cards of a type). Smaller flash
    // than the level-up so frequent stat-ups don't feel as climactic
    // as a full level. Brief coin SFX so the auditory feedback ties
    // back to the existing reward vocabulary.
    //
    // Boost (rebranded Water) doesn't map to a stat any more — its
    // spent_total counter still increments server-side for XP/level
    // math but statLabelForCardType returns null, so we skip the
    // stat-up flash for Water spends.
    if (row?.stat_up) {
      const label = statLabelForCardType(cardKey);
      if (label) {
        const def = STAT_DEF_BY_CARD_TYPE[cardKey];
        setStatUp({
          label,
          tint:  def?.tint  || "#7A4E2D",
          emoji: def?.emoji || "💪",
        });
        setTimeout(() => playKidSfx("coin"), 80);
        setTimeout(() => setStatUp(null), 1200);
      } else if (cardKey === "water") {
        // Water doesn't map to a stat tile — the rollover means
        // the kid just earned +1 to their MAX MANA pool. Flash
        // that explicitly so the new ceiling on the mana bar
        // doesn't feel like it appeared from nowhere.
        setStatUp({
          label: "+1 MAX MANA",
          tint:  "#5DA9E9",
          emoji: "💧",
        });
        setTimeout(() => playKidSfx("coin"), 80);
        setTimeout(() => setStatUp((cur) => (cur?.label === "+1 MAX MANA" ? null : cur)), 1400);
      }
    }

    // Water spend refills mana. When mana_refilled=true, flash a
    // brief +MANA pill so the kid sees the resource bar grow.
    // Skip when stat_up ALSO fired (every 5th water = both new max
    // AND refill) — the bigger "+1 MAX MANA" pill already covers
    // the moment and two pills in 80ms would step on each other.
    if (row?.mana_refilled && !row?.stat_up) {
      setStatUp({
        label: "+MANA",
        tint:  "#5DA9E9",
        emoji: "💧",
      });
      setTimeout(() => playKidSfx("sparkle"), 80);
      setTimeout(() => setStatUp((cur) => (cur?.label === "+MANA" ? null : cur)), 1200);
    }
  };

  return (
    <div style={{
      marginTop: 22, width: "100%", maxWidth: 360,
      display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
    }}>
      <div style={{
        fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
        letterSpacing: "0.16em", color: COLOR.muted,
      }}>
        FEED YOUR PET
      </div>
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
        gap: 8, width: "100%",
      }}>
        {CARDS.map((c) => {
          const count = counts[c.key] ?? 0;
          const empty = count <= 0;
          const isPulsing = pulse === c.key;
          const isBusy = spending === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => spend(c.key)}
              disabled={empty || !!spending}
              aria-label={`${c.label} card, ${count} available`}
              style={{
                position: "relative",
                padding: "10px 6px",
                display: "flex", flexDirection: "column",
                alignItems: "center", gap: 4,
                background: empty
                  ? "rgba(255,255,255,0.4)"
                  : isPulsing
                    ? c.tint
                    : "#fff",
                border: `2px solid ${empty ? COLOR.border : c.tint}`,
                borderRadius: 14,
                opacity: empty ? 0.5 : isBusy ? 0.7 : 1,
                cursor: empty ? "default" : (isBusy ? "wait" : "pointer"),
                touchAction: "manipulation",
                transition: "background 220ms ease, transform 160ms ease",
                transform: isPulsing ? "scale(1.06)" : "scale(1)",
                boxShadow: empty ? "none" : `0 2px 6px ${c.tint}26`,
              }}
            >
              <span style={{ fontSize: 26, lineHeight: 1 }}>{c.emoji}</span>
              <span style={{
                fontFamily: FONT.mono, fontSize: 13, fontWeight: 700,
                color: isPulsing ? "#fff" : (empty ? COLOR.muted : c.tint),
                lineHeight: 1,
              }}>
                ×{count}
              </span>
              <span style={{
                fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
                letterSpacing: "0.08em",
                color: isPulsing ? "#fff" : (empty ? COLOR.muted : COLOR.ink),
                textTransform: "uppercase",
              }}>
                {c.label}
              </span>
            </button>
          );
        })}
      </div>
      {total === 0 && (
        <div style={{
          fontFamily: FONT.forma, fontSize: 12, color: COLOR.muted,
          textAlign: "center", maxWidth: 280, marginTop: 4,
        }}>
          Eat something good — a grown-up can send cards too.
        </div>
      )}

      {/* Level-up flash — appears when spend_kid_card returns
          leveled_up=true. Brief celebration (1.6s) that doesn't
          require a tap to dismiss. Sits above the deck so it's
          unmissable but doesn't block subsequent spends. */}
      {levelUp && (
        <div
          aria-live="polite"
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 320,
            padding: "22px 32px",
            background: "linear-gradient(135deg, #FFD23F 0%, #FF8A3D 100%)",
            color: "#fff",
            borderRadius: 24,
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontSize: 28,
            textAlign: "center",
            boxShadow: "0 14px 36px rgba(0,0,0,0.32)",
            pointerEvents: "none",
            animation: "kid-level-up-pop 380ms cubic-bezier(0.34, 1.56, 0.64, 1)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span style={{ fontSize: 48, lineHeight: 1 }}>⭐</span>
          <span>LEVEL UP!</span>
          <span style={{
            fontFamily: FONT.mono,
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.14em",
          }}>
            LV. {levelUp.to}
          </span>
          <style>{`
            @keyframes kid-level-up-pop {
              from { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
              to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
            }
          `}</style>
        </div>
      )}

      {/* Stat-up flash — small, brief, drawn from the spent card's
          color so the kid associates the stat tier with the card
          type that earned it. Floats above the deck, no tap to
          dismiss. */}
      {statUp && (
        <div
          aria-live="polite"
          style={{
            position: "fixed",
            top: "60%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 310,
            padding: "10px 18px",
            background: statUp.tint,
            color: "#fff",
            borderRadius: 999,
            fontFamily: FONT.mono,
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: "0.14em",
            boxShadow: `0 8px 20px ${statUp.tint}66`,
            pointerEvents: "none",
            animation: "kid-stat-up-pop 320ms cubic-bezier(0.34, 1.56, 0.64, 1)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 18 }}>{statUp.emoji}</span>
          <span>+1 {statUp.label}</span>
          <style>{`
            @keyframes kid-stat-up-pop {
              from { opacity: 0; transform: translate(-50%, -45%) scale(0.6); }
              to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
